// Universal diary codes.
//
// Each firm wrote listings in its own shorthand, and the shorthands clash: "CM" is a committal
// mention at VALS but reads as a contest mention elsewhere. Staff now type one of these codes
// in the diary instead, the same at every firm. Each code names the VLA fee the listing uses
// up, so the reminders and fee checks read the code rather than guess from free text.
//
// A listing with more than one event is written with a slash: "PLEA/CMEN".
// Whether a fee is paid at the single or consolidated rate comes from the grant, not the code.

export const DIARY_CODES = [
  { code: "MEN", label: "Mention", fee: "Summary crime mention / appearance", group: "Mentions" },
  { code: "FMEN", label: "Further mention", fee: "Summary crime mention / appearance", group: "Mentions" },
  { code: "SMEN", label: "Special mention", fee: "Special mention - summary", group: "Mentions" },
  { code: "CMEN", label: "Contest mention", fee: "Contest mention", stage: "Contest mention", listing: "Contest Mention", group: "Contest" },
  { code: "PCM", label: "Plea at contest mention", fee: "Plea at contest mention", group: "Contest" },
  { code: "PLEA", label: "Plea", fee: "Appearance (plea)", listing: "Plea", group: "Plea and sentence" },
  { code: "SI", label: "Sentence indication", fee: "Brief sentence indication", group: "Plea and sentence" },
  { code: "SENT", label: "Sentence, incl. return for sentence", fee: "Appear at sentence", listing: "Sentence", group: "Plea and sentence" },
  { code: "CONT", label: "Contested hearing (each day)", fee: "Defend/contest daily fee", stage: "Contested hearing", listing: "Contested Hearing", group: "Contest" },
  { code: "BAIL", label: "Bail application", fee: "Bail appearance (Table B)", listing: "Bail Application", group: "Bail and remand" },
  { code: "FRH", label: "First remand hearing", fee: "First remand hearing", group: "Bail and remand" },
  { code: "DRUG", label: "Drug / CREDIT court mention", fee: "CRED/drug court mention", group: "Mentions" },
  { code: "SJ", label: "Summary jurisdiction application", fee: "Application for summary jurisdiction", group: "Indictable" },
  // A committal mention is not the committal: only a matter adjourned to a committal or a
  // contested committal needs an extension (Emma, 22 Oct 2026).
  { code: "COMM", label: "Committal mention", fee: "Committal mention", stage: "Committal mention", listing: "Committal", group: "Indictable" },
  { code: "CCON", label: "Committal contest", fee: "Committal contest", stage: "Committal", listing: "Committal", group: "Indictable" },
  { code: "DH", label: "Directions hearing (County / Supreme)", fee: "Directions hearing", group: "Indictable" },
  { code: "ARR", label: "Arraignment", fee: "", group: "Indictable" },
  { code: "TRIAL", label: "Trial (each day)", fee: "Trial appearance", stage: "Trial", listing: "Trial", group: "Indictable" },
  { code: "APP", label: "Appeal", fee: "Appeal appearance (needs its own grant)", stage: "Appeal", listing: "Appeal", group: "Appeal and Children's Court" },
  { code: "GC", label: "Group conference", fee: "Group conference", group: "Appeal and Children's Court" },
  { code: "ARC", label: "ARC ratification / review", fee: "ARC ratification / review hearing", group: "Appeal and Children's Court" },
  { code: "YCO", label: "YCO planning / monitoring / breach", fee: "YCO planning, monitoring or breach", group: "Appeal and Children's Court" },
];

// The order the groups are listed in, on the code sheet and in each diary's list.
export const DIARY_CODE_GROUPS = [
  "Mentions",
  "Contest",
  "Plea and sentence",
  "Bail and remand",
  "Indictable",
  "Appeal and Children's Court",
];

const BY_CODE = new Map(DIARY_CODES.map((entry) => [entry.code, entry]));
const BY_LABEL = new Map(DIARY_CODES.flatMap((entry) => [
  [entry.label.toUpperCase(), entry],
  // The label without its detail: "Contested hearing (each day)" is also "Contested hearing".
  [entry.label.replace(/\s*\(.*\)|,.*$/g, "").toUpperCase(), entry],
]));

// Codes the firms used before, read so earlier weeks still work. Each is shown as "old code"
// with the universal one to use. Codes with no settled meaning (RoD, FVCD, D, HATISLOW …)
// are not read at all.
export const LEGACY_DIARY_CODES = {
  M: "MEN",
  MENTION: "MEN",
  FM: "FMEN",
  FME: "FMEN",
  "FURTHER MENTION": "FMEN",
  "SPECIAL MENTION": "SMEN",
  CME: "CMEN",
  CM: "COMM",
  PG: "PLEA",
  "CONSOL PG": "PLEA",
  SENTENCE: "SENT",
  RFS: "SENT",
  "RETURN FOR SENTENCE": "SENT",
  H: "CONT",
  SH: "CONT",
  B: "BAIL",
  A: "APP",
  T: "TRIAL",
  ARRAIGNMENT: "ARR",
};

// Which stages come first when a listing has more than one event: the one furthest from a
// first grant decides the reminder.
const STAGE_ORDER = ["Appeal", "Trial", "Committal", "Contested hearing", "Contest mention", "Committal mention"];

// Stages a summary grant already covers: listing them needs no extension or new grant. Every
// summary grant funds contest mentions, and a committal mention is not the committal (Emma,
// 22 Oct 2026). They are still stages of their own for where an extension's cover ends.
export const STAGES_COVERED_BY_GRANT = new Set(["Contest mention", "Committal mention"]);

// A diary's appearance text, read as codes. Returns the codes found, the old codes read along
// the way ({ written, code }), and anything that is not a code.
export function readDiaryCode(text = "") {
  const value = String(text || "").trim();
  const result = { codes: [], legacy: [], unknown: [] };
  if (!value) return result;
  for (const part of value.split(/\s*[/&+,]\s*/)) {
    const token = part.trim().toUpperCase().replace(/\s+/g, " ");
    if (!token) continue;
    const direct = BY_CODE.get(token) || BY_LABEL.get(token);
    const legacyCode = LEGACY_DIARY_CODES[token];
    const entry = direct || (legacyCode ? BY_CODE.get(legacyCode) : null);
    if (!entry) {
      result.unknown.push(part.trim());
      continue;
    }
    if (!direct) result.legacy.push({ written: part.trim(), code: entry.code });
    if (!result.codes.some((found) => found.code === entry.code)) result.codes.push(entry);
  }
  return result;
}

export function diaryCodeFor(code) {
  return BY_CODE.get(String(code || "").toUpperCase()) || null;
}

// The stage beyond a first summary grant that a listing reaches, if any, from its codes.
export function stageForDiaryCodes(text = "") {
  const stages = readDiaryCode(text).codes.map((entry) => entry.stage).filter(Boolean);
  return STAGE_ORDER.find((stage) => stages.includes(stage)) || "";
}

// What the reminders do when a code is listed: an "Extension check" for a stage beyond a
// first summary grant, a "New grant" for an appeal, otherwise nothing.
export function reminderForDiaryCode(entry) {
  if (!entry?.stage || STAGES_COVERED_BY_GRANT.has(entry.stage)) return "";
  return entry.stage === "Appeal" ? "New grant" : "Extension check";
}

// The firms' old short codes, grouped under the code that replaces each one, for the sheet.
export function legacyCodesByCode() {
  const byCode = new Map();
  for (const [old, code] of Object.entries(LEGACY_DIARY_CODES)) {
    if (/\s/.test(old) || old.length > 4 || BY_LABEL.has(old)) continue;
    byCode.set(code, [...(byCode.get(code) || []), old]);
  }
  return [...byCode.entries()].map(([code, old]) => ({ code, old }));
}
